import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumericInput } from '../components/ui/numeric-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Plus, MapPin, Phone, Users, TrendingUp, 
  Fuel, AlertTriangle, Settings, Eye, LogIn, Check,
  CreditCard, IndianRupee, Calendar, Bell, Droplets,
  ChevronRight, X, BarChart3, FileText, Award, Download,
  BellOff, Trophy, Target
} from 'lucide-react';

// Indian date formatting
const formatDateIndian = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
};

const formatDateTimeIndian = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata'
  });
};

function MultiStationDashboard() {
  const [stations, setStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [stationStats, setStationStats] = useState({});
  const [addDialog, setAddDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPumpId, setCurrentPumpId] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState(null);
  const [addStep, setAddStep] = useState(1); // 1: Basic, 2: Location, 3: Infrastructure
  const [activeTab, setActiveTab] = useState('overview');
  const [consolidatedReport, setConsolidatedReport] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('7d');
  const [reportDateRange, setReportDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
    end: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
  });
  const { login, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [newStation, setNewStation] = useState({
    // Basic Info
    pump_name: '',
    owner_name: '',
    dealership: 'BPCL',
    owner_whatsapp: '',
    // Location
    location: '',
    town: '',
    district: '',
    state: '',
    // Contact
    mobile: '',
    email: '',
    // Infrastructure
    hsd_dispensers: 4,
    ms_dispensers: 4,
    hsd_tanks: 2,
    ms_tanks: 2,
    hsd_tank_capacity: 10000,
    ms_tank_capacity: 10000
  });

  useEffect(() => {
    fetchStations();
    fetchCurrentPump();
    fetchOwnerInfo();
  }, []);

  const fetchCurrentPump = async () => {
    try {
      const res = await axios.get(`${API}/pump-profile`);
      setCurrentPumpId(res.data.id);
    } catch (error) {
      console.error('Failed to fetch current pump');
    }
  };

  const fetchOwnerInfo = async () => {
    try {
      const res = await axios.get(`${API}/multi-station/owner-info`);
      setOwnerInfo(res.data);
      // Pre-fill owner info
      setNewStation(prev => ({
        ...prev,
        owner_name: res.data.owner_name || '',
        owner_whatsapp: res.data.owner_whatsapp || '',
        email: res.data.email || '',
        dealership: res.data.dealership || 'BPCL',
        state: res.data.state || ''
      }));
    } catch (error) {
      console.error('Failed to fetch owner info');
    }
  };

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API}/multi-station/stations`);
      setStations(res.data.stations);
      
      // Fetch stats for each station
      const statsPromises = res.data.stations.map(station => 
        axios.get(`${API}/multi-station/stations/${station.id}/stats`)
          .then(r => ({ id: station.id, stats: r.data }))
          .catch(() => ({ id: station.id, stats: null }))
      );
      
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(s => { statsMap[s.id] = s.stats; });
      setStationStats(statsMap);
    } catch (error) {
      if (error.response?.status === 404) {
        setStations([]);
      } else {
        toast.error('Failed to load stations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddStation = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/multi-station/stations`, newStation);
      toast.success(`Station added! Default PIN: ${res.data.temp_pin}`);
      setAddDialog(false);
      setAddStep(1);
      resetNewStation();
      fetchStations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add station');
    }
  };

  const resetNewStation = () => {
    setNewStation({
      pump_name: '',
      owner_name: ownerInfo?.owner_name || '',
      dealership: ownerInfo?.dealership || 'BPCL',
      owner_whatsapp: ownerInfo?.owner_whatsapp || '',
      location: '',
      town: '',
      district: '',
      state: ownerInfo?.state || '',
      mobile: '',
      email: ownerInfo?.email || '',
      hsd_dispensers: 4,
      ms_dispensers: 4,
      hsd_tanks: 2,
      ms_tanks: 2,
      hsd_tank_capacity: 10000,
      ms_tank_capacity: 10000
    });
  };

  // Validation for each step
  const validateStep = (step) => {
    if (step === 1) {
      if (!newStation.pump_name?.trim()) {
        toast.error('Station name is required');
        return false;
      }
      if (!newStation.owner_name?.trim()) {
        toast.error('Owner name is required');
        return false;
      }
      if (!newStation.mobile?.trim() || newStation.mobile.length < 10) {
        toast.error('Valid mobile number is required');
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!newStation.location?.trim()) {
        toast.error('Location/Address is required');
        return false;
      }
      if (!newStation.town?.trim()) {
        toast.error('Town/City is required');
        return false;
      }
      if (!newStation.district?.trim()) {
        toast.error('District is required');
        return false;
      }
      if (!newStation.state?.trim()) {
        toast.error('State is required');
        return false;
      }
      return true;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep(addStep)) {
      setAddStep(addStep + 1);
    }
  };

  const handleLoginToStation = async (station) => {
    try {
      const res = await axios.post(`${API}/multi-station/stations/${station.id}/login`);
      // Direct login with new token
      login(res.data.token, res.data.user);
      toast.success(`Switched to ${res.data.pump_name}`);
      navigate('/');
      // Force page reload to refresh all data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch station');
    }
  };

  const handleMarkAlertRead = async (alertId, stationId) => {
    try {
      await axios.post(`${API}/multi-station/alerts/${alertId}/read`);
      // Refresh stats for that station
      const res = await axios.get(`${API}/multi-station/stations/${stationId}/stats`);
      setStationStats(prev => ({ ...prev, [stationId]: res.data }));
      toast.success('Alert marked as read');
    } catch (error) {
      toast.error('Failed to mark alert as read');
    }
  };

  const fetchConsolidatedReport = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/multi-station/consolidated-report?start_date=${reportDateRange.start}&end_date=${reportDateRange.end}`);
      setConsolidatedReport(res.data);
    } catch (error) {
      toast.error('Failed to load consolidated report');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/multi-station/comparative-analytics?period=${reportPeriod}`);
      setAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleBulkDismissAlerts = async () => {
    try {
      const res = await axios.post(`${API}/multi-station/bulk-dismiss-alerts`);
      toast.success(`Dismissed ${res.data.dismissed_count} alerts across ${res.data.station_count} stations`);
      fetchStations(); // Refresh stats
    } catch (error) {
      toast.error('Failed to dismiss alerts');
    }
  };

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchConsolidatedReport();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [activeTab, reportDateRange, reportPeriod]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const StationCard = ({ station }) => {
    const stats = stationStats[station.id];
    const isCurrentStation = station.id === currentPumpId;
    const hasAlerts = stats?.recent_alerts && stats.recent_alerts.length > 0;
    const [profitPeriod, setProfitPeriod] = useState('day');
    const [sellCollectionPeriod, setSellCollectionPeriod] = useState('day');
    
    const getProfitData = () => {
      if (!stats?.profit) return null;
      return stats.profit[profitPeriod];
    };
    
    const getSellVsCollectionData = () => {
      if (!stats?.sell_vs_collection) return null;
      return stats.sell_vs_collection[sellCollectionPeriod];
    };
    
    const profitData = getProfitData();
    const sellCollectionData = getSellVsCollectionData();
    
    return (
      <Card className={`flex flex-col h-full border hover:shadow-lg transition-shadow ${isCurrentStation ? 'border-primary bg-primary/5' : 'border-border'}`} data-testid={`station-card-${station.id}`}>
        {/* Header - Fixed height */}
        <div className="p-4 border-b border-border/50">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-heading font-bold">{station.pump_name}</h3>
                {isCurrentStation && (
                  <span className="px-2 py-0.5 bg-primary text-white text-xs rounded-full">Current</span>
                )}
              </div>
              <div className="flex items-center text-sm text-muted-foreground mt-1">
                <MapPin className="w-4 h-4 mr-1" />
                {station.town}, {station.district}
              </div>
            </div>
            <span className={`px-2 py-1 rounded text-xs font-semibold ${
              station.account_status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}>
              {station.account_status || 'active'}
            </span>
          </div>
        </div>

        {/* Content - Flex grow */}
        <div className="flex-1 p-4">
          {stats ? (
            <div className="space-y-3">
              {/* Sell vs Collection Section with Period Toggle */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-3 rounded-lg border border-blue-100 dark:border-blue-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">SELL vs COLLECTION</span>
                  <div className="flex gap-1">
                    {['day', 'week', 'month'].map(period => (
                      <button
                        key={period}
                        onClick={(e) => { e.stopPropagation(); setSellCollectionPeriod(period); }}
                        className={`px-2 py-0.5 text-xs rounded transition-colors ${
                          sellCollectionPeriod === period 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-200'
                        }`}
                      >
                        {period === 'day' ? 'Day' : period === 'week' ? 'Week' : 'Month'}
                      </button>
                    ))}
                  </div>
                </div>
                {sellCollectionData ? (
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Sell</p>
                      <p className="font-bold text-sm text-emerald-600">₹{sellCollectionData.sell?.toLocaleString('en-IN') || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Collection</p>
                      <p className="font-bold text-sm text-blue-600">₹{sellCollectionData.collection?.toLocaleString('en-IN') || 0}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">Gap</p>
                      <p className={`font-bold text-sm ${sellCollectionData.difference > 0 ? 'text-amber-600' : sellCollectionData.difference < 0 ? 'text-emerald-600' : ''}`}>
                        {sellCollectionData.difference > 0 ? '+' : ''}₹{sellCollectionData.difference?.toLocaleString('en-IN') || 0}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-2">No data available</p>
                )}
              </div>

              {/* Stock Section */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 p-3 rounded-lg border border-amber-100 dark:border-amber-800">
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300">CURRENT STOCK</span>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-200 dark:bg-amber-700 flex items-center justify-center">
                      <Fuel className="w-4 h-4 text-amber-700 dark:text-amber-200" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">MS (Petrol)</p>
                      <p className="font-bold text-sm">{stats.ms_stock?.toLocaleString() || 0} L</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-200 dark:bg-blue-700 flex items-center justify-center">
                      <Fuel className="w-4 h-4 text-blue-700 dark:text-blue-200" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">HSD (Diesel)</p>
                      <p className="font-bold text-sm">{stats.hsd_stock?.toLocaleString() || 0} L</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profit Section with Period Toggle */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/50 p-3 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground">PROFIT</span>
                  <div className="flex gap-1">
                    {['day', 'week', 'month'].map(period => (
                      <button
                        key={period}
                        onClick={(e) => { e.stopPropagation(); setProfitPeriod(period); }}
                        className={`px-2 py-0.5 text-xs rounded transition-colors ${
                          profitPeriod === period 
                            ? 'bg-primary text-white' 
                            : 'bg-secondary hover:bg-secondary/80 text-muted-foreground'
                        }`}
                      >
                        {period === 'day' ? 'Day' : period === 'week' ? 'Week' : 'Month'}
                      </button>
                    ))}
                  </div>
                </div>
                {profitData ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Gross Profit</p>
                      <p className={`font-bold text-sm ${profitData.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {profitData.gross_profit >= 0 ? '' : '-'}₹{Math.abs(profitData.gross_profit).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Net Profit</p>
                      <p className={`font-bold text-sm ${profitData.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {profitData.net_profit >= 0 ? '' : '-'}₹{Math.abs(profitData.net_profit).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-2">No profit data</p>
                )}
              </div>

              {/* Loss Stats */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded-lg text-center ${stats.ms_loss_7d > 0 ? 'bg-red-50 dark:bg-red-900/30' : 'bg-secondary/50'}`}>
                  <p className="text-xs text-muted-foreground">MS Loss (7d)</p>
                  <p className={`font-bold text-sm ${stats.ms_loss_7d > 0 ? 'text-red-600' : ''}`}>{stats.ms_loss_7d?.toFixed(0) || 0} L</p>
                </div>
                <div className={`p-2 rounded-lg text-center ${stats.hsd_loss_7d > 0 ? 'bg-red-50 dark:bg-red-900/30' : 'bg-secondary/50'}`}>
                  <p className="text-xs text-muted-foreground">HSD Loss (7d)</p>
                  <p className={`font-bold text-sm ${stats.hsd_loss_7d > 0 ? 'text-red-600' : ''}`}>{stats.hsd_loss_7d?.toFixed(0) || 0} L</p>
                </div>
              </div>

              {/* Alerts Section - Fixed max height with scroll */}
              <div className={`p-2.5 rounded-lg border ${hasAlerts ? 'bg-red-50 border-red-200' : 'bg-secondary/30 border-transparent'}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Bell className={`w-3.5 h-3.5 ${hasAlerts ? 'text-red-600' : 'text-muted-foreground'}`} />
                  <span className={`text-xs font-semibold ${hasAlerts ? 'text-red-700' : 'text-muted-foreground'}`}>
                    {hasAlerts ? `${stats.unread_alerts} Unread Alerts` : 'No Alerts'}
                  </span>
                </div>
                {hasAlerts && (
                  <div className="max-h-16 overflow-y-auto space-y-1">
                    {stats.recent_alerts.slice(0, 2).map(alert => (
                      <div key={alert.id} className="flex items-center justify-between gap-2 text-xs bg-white/50 p-1.5 rounded">
                        <p className="text-red-800 truncate flex-1">{alert.message}</p>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-5 w-5 p-0 hover:bg-red-100"
                          onClick={(e) => { e.stopPropagation(); handleMarkAlertRead(alert.id, station.id); }}
                        >
                          <Check className="w-3 h-3 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              No data available
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="p-4 pt-0 mt-auto">
          <div className="flex gap-2">
            <Button 
              size="sm" 
              variant="outline" 
              className="flex-1 h-9"
              onClick={() => setSelectedStation(station)}
            >
              <Eye className="w-4 h-4 mr-1.5" />
              Details
            </Button>
            {!isCurrentStation && (
              <Button 
                size="sm" 
                className="flex-1 h-9"
                onClick={() => handleLoginToStation(station)}
                data-testid={`login-station-${station.id}`}
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                Switch
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  // Calculate totals across all stations
  const totalStats = Object.values(stationStats).reduce((acc, stats) => {
    if (!stats) return acc;
    return {
      totalRevenue: (acc.totalRevenue || 0) + (stats.yesterday_revenue || 0),
      totalMsStock: (acc.totalMsStock || 0) + (stats.ms_stock || 0),
      totalHsdStock: (acc.totalHsdStock || 0) + (stats.hsd_stock || 0),
      totalAlerts: (acc.totalAlerts || 0) + (stats.unread_alerts || 0),
      totalCreditOutstanding: (acc.totalCreditOutstanding || 0) + (stats.credit_outstanding || 0),
      totalCreditCollected7d: (acc.totalCreditCollected7d || 0) + (stats.credit_collected_7d || 0),
      totalCreditCollectedMonth: (acc.totalCreditCollectedMonth || 0) + (stats.credit_collected_month || 0),
      totalMsLoss: (acc.totalMsLoss || 0) + (stats.ms_loss_7d || 0),
      totalHsdLoss: (acc.totalHsdLoss || 0) + (stats.hsd_loss_7d || 0)
    };
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="multi-station-page">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Multi-Station Management</h1>
            <p className="text-muted text-sm">Centralized control for all your fuel stations</p>
          </div>
          <Dialog open={addDialog} onOpenChange={(open) => { setAddDialog(open); if (!open) setAddStep(1); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-station-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Station
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add New Station</DialogTitle>
                <DialogDescription>
                  Step {addStep} of 3: {addStep === 1 ? 'Basic Information' : addStep === 2 ? 'Location Details' : 'Infrastructure'}
                </DialogDescription>
              </DialogHeader>

              {/* Progress indicator */}
              <div className="flex items-center gap-2 mb-4">
                {[1, 2, 3].map(step => (
                  <div key={step} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      addStep >= step ? 'bg-primary text-white' : 'bg-secondary text-muted'
                    }`}>
                      {step}
                    </div>
                    {step < 3 && <ChevronRight className="w-4 h-4 text-muted mx-1" />}
                  </div>
                ))}
              </div>

              <form onSubmit={handleAddStation}>
                {/* Step 1: Basic Info */}
                {addStep === 1 && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Station Name *</label>
                      <Input
                        value={newStation.pump_name}
                        onChange={(e) => setNewStation({ ...newStation, pump_name: e.target.value })}
                        placeholder="e.g., ABC Fuel Station"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Owner Name *</label>
                        <Input
                          value={newStation.owner_name}
                          onChange={(e) => setNewStation({ ...newStation, owner_name: e.target.value })}
                          placeholder="Owner's full name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Dealership</label>
                        <Select 
                          value={newStation.dealership} 
                          onValueChange={(v) => setNewStation({ ...newStation, dealership: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BPCL">BPCL</SelectItem>
                            <SelectItem value="HPCL">HPCL</SelectItem>
                            <SelectItem value="IOCL">IOCL</SelectItem>
                            <SelectItem value="Jio-BP">Jio-BP</SelectItem>
                            <SelectItem value="Nayara">Nayara</SelectItem>
                            <SelectItem value="Reliance Petroleum">Reliance Petroleum</SelectItem>
                            <SelectItem value="Shell">Shell</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Mobile Number *</label>
                        <Input
                          value={newStation.mobile}
                          onChange={(e) => setNewStation({ ...newStation, mobile: e.target.value })}
                          placeholder="10-digit mobile"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Owner WhatsApp</label>
                        <Input
                          value={newStation.owner_whatsapp}
                          onChange={(e) => setNewStation({ ...newStation, owner_whatsapp: e.target.value })}
                          placeholder="WhatsApp number"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <Input
                        type="email"
                        value={newStation.email}
                        onChange={(e) => setNewStation({ ...newStation, email: e.target.value })}
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Location */}
                {addStep === 2 && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Address/Location *</label>
                      <Input
                        value={newStation.location}
                        onChange={(e) => setNewStation({ ...newStation, location: e.target.value })}
                        placeholder="Full address"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Town/City *</label>
                        <Input
                          value={newStation.town}
                          onChange={(e) => setNewStation({ ...newStation, town: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">District *</label>
                        <Input
                          value={newStation.district}
                          onChange={(e) => setNewStation({ ...newStation, district: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">State *</label>
                      <Input
                        value={newStation.state}
                        onChange={(e) => setNewStation({ ...newStation, state: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: Infrastructure */}
                {addStep === 3 && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted">Dispensers</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">MS (Petrol) Dispensers</label>
                        <NumericInput
                          value={newStation.ms_dispensers}
                          onChange={(e) => setNewStation({ ...newStation, ms_dispensers: parseInt(e.target.value) || 0 })}
                          min={0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">HSD (Diesel) Dispensers</label>
                        <NumericInput
                          value={newStation.hsd_dispensers}
                          onChange={(e) => setNewStation({ ...newStation, hsd_dispensers: parseInt(e.target.value) || 0 })}
                          min={0}
                        />
                      </div>
                    </div>
                    <h4 className="font-medium text-sm text-muted mt-4">Storage Tanks</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">MS Tanks</label>
                        <NumericInput
                          value={newStation.ms_tanks}
                          onChange={(e) => setNewStation({ ...newStation, ms_tanks: parseInt(e.target.value) || 0 })}
                          min={0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">HSD Tanks</label>
                        <NumericInput
                          value={newStation.hsd_tanks}
                          onChange={(e) => setNewStation({ ...newStation, hsd_tanks: parseInt(e.target.value) || 0 })}
                          min={0}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">MS Tank Capacity (L)</label>
                        <NumericInput
                          value={newStation.ms_tank_capacity}
                          onChange={(e) => setNewStation({ ...newStation, ms_tank_capacity: parseInt(e.target.value) || 0 })}
                          min={0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">HSD Tank Capacity (L)</label>
                        <NumericInput
                          value={newStation.hsd_tank_capacity}
                          onChange={(e) => setNewStation({ ...newStation, hsd_tank_capacity: parseInt(e.target.value) || 0 })}
                          min={0}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <DialogFooter className="mt-6 gap-2">
                  <Button type="button" variant="outline" onClick={() => { 
                    if (addStep > 1) setAddStep(addStep - 1);
                    else { setAddDialog(false); setAddStep(1); }
                  }}>
                    {addStep > 1 ? 'Back' : 'Cancel'}
                  </Button>
                  {addStep < 3 ? (
                    <Button type="button" onClick={handleNextStep}>
                      Next
                    </Button>
                  ) : (
                    <Button type="submit">Add Station</Button>
                  )}
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Tabs for different views */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="flex justify-between items-center mb-4">
                <TabsList>
                  <TabsTrigger value="overview" data-testid="overview-tab">
                    <Building2 className="w-4 h-4 mr-2" />
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="reports" data-testid="reports-tab">
                    <FileText className="w-4 h-4 mr-2" />
                    Reports
                  </TabsTrigger>
                  <TabsTrigger value="analytics" data-testid="analytics-tab">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Analytics
                  </TabsTrigger>
                </TabsList>

                {totalStats.totalAlerts > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleBulkDismissAlerts}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="bulk-dismiss-alerts-btn"
                  >
                    <BellOff className="w-4 h-4 mr-2" />
                    Dismiss All Alerts ({totalStats.totalAlerts})
                  </Button>
                )}
              </div>

              {/* OVERVIEW TAB */}
              <TabsContent value="overview" className="space-y-6">
                {/* Summary Cards - Row 1 */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="stat-card">
                    <div className="flex items-center gap-3">
                      <div className="stat-card-icon bg-blue-100">
                        <Building2 className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="stat-card-label">Total Stations</p>
                        <p className="stat-card-value">{stations.length}</p>
                      </div>
                    </div>
                  </Card>
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="stat-card-icon bg-emerald-100">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="stat-card-label">Yesterday's Revenue</p>
                    <p className="stat-card-value text-emerald-600">{formatCurrency(totalStats.totalRevenue)}</p>
                  </div>
                </div>
              </Card>
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="stat-card-icon bg-amber-100">
                    <CreditCard className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="stat-card-label">Credit Outstanding</p>
                    <p className="stat-card-value text-amber-600">{formatCurrency(totalStats.totalCreditOutstanding)}</p>
                  </div>
                </div>
              </Card>
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="stat-card-icon bg-emerald-100">
                    <IndianRupee className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="stat-card-label">Credit (7 Days)</p>
                    <p className="stat-card-value text-emerald-600">{formatCurrency(totalStats.totalCreditCollected7d)}</p>
                  </div>
                </div>
              </Card>
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="stat-card-icon bg-blue-100">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="stat-card-label">Credit (Month)</p>
                    <p className="stat-card-value text-blue-600">{formatCurrency(totalStats.totalCreditCollectedMonth)}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Summary Cards - Row 2 */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="stat-card-icon bg-amber-100">
                    <Fuel className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="stat-card-label">Total MS Stock</p>
                    <p className="stat-card-value">{totalStats.totalMsStock?.toLocaleString() || 0} L</p>
                  </div>
                </div>
              </Card>
              <Card className="stat-card">
                <div className="flex items-center gap-3">
                  <div className="stat-card-icon bg-blue-100">
                    <Fuel className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="stat-card-label">Total HSD Stock</p>
                    <p className="stat-card-value">{totalStats.totalHsdStock?.toLocaleString() || 0} L</p>
                  </div>
                </div>
              </Card>
              <Card className={`stat-card ${totalStats.totalMsLoss > 0 ? 'border-red-200 bg-red-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className={`stat-card-icon ${totalStats.totalMsLoss > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                    <Droplets className={`w-5 h-5 ${totalStats.totalMsLoss > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="stat-card-label">MS Loss (7d)</p>
                    <p className={`stat-card-value ${totalStats.totalMsLoss > 0 ? 'text-red-600' : ''}`}>
                      {totalStats.totalMsLoss?.toFixed(0) || 0} L
                    </p>
                  </div>
                </div>
              </Card>
              <Card className={`stat-card ${totalStats.totalHsdLoss > 0 ? 'border-red-200 bg-red-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className={`stat-card-icon ${totalStats.totalHsdLoss > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                    <Droplets className={`w-5 h-5 ${totalStats.totalHsdLoss > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="stat-card-label">HSD Loss (7d)</p>
                    <p className={`stat-card-value ${totalStats.totalHsdLoss > 0 ? 'text-red-600' : ''}`}>
                      {totalStats.totalHsdLoss?.toFixed(0) || 0} L
                    </p>
                  </div>
                </div>
              </Card>
              <Card className={`stat-card ${totalStats.totalAlerts > 0 ? 'border-red-200 bg-red-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className={`stat-card-icon ${totalStats.totalAlerts > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                    <AlertTriangle className={`w-5 h-5 ${totalStats.totalAlerts > 0 ? 'text-red-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="stat-card-label">Total Alerts</p>
                    <p className={`stat-card-value ${totalStats.totalAlerts > 0 ? 'text-red-600' : ''}`}>
                      {totalStats.totalAlerts || 0}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Stations Grid */}
            {stations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 auto-rows-fr">
                {stations.map(station => (
                  <StationCard key={station.id} station={station} />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border border-dashed border-border">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-heading font-semibold mb-2">No Stations Added</h3>
                <p className="text-muted-foreground mb-4">Add your first fuel station to get started with multi-station management.</p>
                <Button onClick={() => setAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Station
                </Button>
              </Card>
            )}
              </TabsContent>

              {/* REPORTS TAB */}
              <TabsContent value="reports" className="space-y-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <h3 className="text-lg font-heading font-semibold">Consolidated Station Report</h3>
                    <p className="text-muted text-sm">View aggregated data across all your stations</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input
                      type="date"
                      value={reportDateRange.start}
                      onChange={(e) => setReportDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-40"
                    />
                    <span className="text-muted">to</span>
                    <Input
                      type="date"
                      value={reportDateRange.end}
                      onChange={(e) => setReportDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-40"
                    />
                    <Button variant="outline" onClick={fetchConsolidatedReport}>
                      <Download className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>

                {analyticsLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : consolidatedReport ? (
                  <div className="space-y-6">
                    {/* Totals Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Total Sales</p>
                        <p className="text-xl font-bold text-emerald-600">{formatCurrency(consolidatedReport.totals?.total_sales_value)}</p>
                      </Card>
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Total Purchases</p>
                        <p className="text-xl font-bold text-blue-600">{formatCurrency(consolidatedReport.totals?.total_purchases_value)}</p>
                      </Card>
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Total Expenses</p>
                        <p className="text-xl font-bold text-amber-600">{formatCurrency(consolidatedReport.totals?.total_expenses)}</p>
                      </Card>
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Total Fuel Loss</p>
                        <p className="text-xl font-bold text-red-600">{formatCurrency(consolidatedReport.totals?.total_loss_value)}</p>
                      </Card>
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Credit Collected</p>
                        <p className="text-xl font-bold text-green-600">{formatCurrency(consolidatedReport.totals?.total_credit_collected)}</p>
                      </Card>
                    </div>

                    {/* Station-wise Breakdown */}
                    <Card className="border border-border overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary border-b border-border">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Station</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Sales</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Purchases</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Expenses</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Fuel Loss</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Credit Out.</th>
                            <th className="px-4 py-3 text-center text-xs font-bold text-muted uppercase">Alerts</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {consolidatedReport.stations?.map(station => (
                            <tr key={station.station_id} className="hover:bg-secondary/50">
                              <td className="px-4 py-3">
                                <p className="font-medium">{station.station_name}</p>
                                <p className="text-xs text-muted">{station.location}</p>
                              </td>
                              <td className="px-4 py-3 text-right text-sm">{formatCurrency(station.sales?.total_value)}</td>
                              <td className="px-4 py-3 text-right text-sm">{formatCurrency(station.purchases?.total_value)}</td>
                              <td className="px-4 py-3 text-right text-sm">{formatCurrency(station.expenses?.total)}</td>
                              <td className="px-4 py-3 text-right text-sm text-red-600">
                                {station.fuel_loss?.ms_loss?.toFixed(0)}L + {station.fuel_loss?.hsd_loss?.toFixed(0)}L
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-amber-600">{formatCurrency(station.credit?.outstanding)}</td>
                              <td className="px-4 py-3 text-center">
                                {station.alerts?.unread > 0 ? (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                    {station.alerts.unread}
                                  </span>
                                ) : (
                                  <span className="text-green-600 text-xs">✓</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                ) : (
                  <Card className="p-12 text-center border border-dashed border-border">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-heading font-semibold mb-2">No Report Data</h3>
                    <p className="text-muted-foreground">Select a date range and click Refresh to load the report.</p>
                  </Card>
                )}
              </TabsContent>

              {/* ANALYTICS TAB */}
              <TabsContent value="analytics" className="space-y-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <h3 className="text-lg font-heading font-semibold">Comparative Analytics</h3>
                    <p className="text-muted text-sm">Benchmark performance across all stations</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={reportPeriod} onValueChange={setReportPeriod}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7d">Last 7 Days</SelectItem>
                        <SelectItem value="30d">Last 30 Days</SelectItem>
                        <SelectItem value="90d">Last 90 Days</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={fetchAnalytics}>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>

                {analyticsLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : analytics ? (
                  <div className="space-y-6">
                    {/* Benchmarks */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Avg Daily Revenue</p>
                        <p className="text-xl font-bold">{formatCurrency(analytics.benchmarks?.avg_daily_revenue)}</p>
                      </Card>
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Avg Loss %</p>
                        <p className="text-xl font-bold text-amber-600">{analytics.benchmarks?.avg_loss_percentage?.toFixed(2)}%</p>
                      </Card>
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Avg Expense Ratio</p>
                        <p className="text-xl font-bold">{analytics.benchmarks?.avg_expense_ratio?.toFixed(2)}%</p>
                      </Card>
                      <Card className="p-4 border border-border">
                        <p className="text-xs text-muted">Avg Credit Collection</p>
                        <p className="text-xl font-bold text-green-600">{analytics.benchmarks?.avg_credit_collection_rate?.toFixed(1)}%</p>
                      </Card>
                    </div>

                    {/* Top Performer */}
                    {analytics.top_performer && (
                      <Card className="p-4 border border-emerald-200 bg-emerald-50">
                        <div className="flex items-center gap-3">
                          <Trophy className="w-8 h-8 text-emerald-600" />
                          <div>
                            <p className="text-sm text-emerald-700">Top Performer</p>
                            <p className="text-lg font-bold text-emerald-800">{analytics.top_performer}</p>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Needs Attention */}
                    {analytics.needs_attention?.length > 0 && (
                      <Card className="p-4 border border-amber-200 bg-amber-50">
                        <div className="flex items-center gap-3">
                          <Target className="w-6 h-6 text-amber-600" />
                          <div>
                            <p className="text-sm text-amber-700">Needs Attention (High Loss or Expense)</p>
                            <p className="font-medium text-amber-800">{analytics.needs_attention.join(', ')}</p>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Station Rankings */}
                    <Card className="border border-border overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-secondary border-b border-border">
                          <tr>
                            <th className="px-4 py-3 text-center text-xs font-bold text-muted uppercase">Rank</th>
                            <th className="px-4 py-3 text-left text-xs font-bold text-muted uppercase">Station</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Daily Avg Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Total Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Loss %</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Expense %</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-muted uppercase">Credit Collection</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {analytics.analytics?.map(station => (
                            <tr key={station.station_id} className={`hover:bg-secondary/50 ${station.rank === 1 ? 'bg-emerald-50' : ''}`}>
                              <td className="px-4 py-3 text-center">
                                {station.rank === 1 ? (
                                  <Award className="w-5 h-5 text-amber-500 mx-auto" />
                                ) : (
                                  <span className="font-bold text-muted">{station.rank}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 font-medium">{station.station_name}</td>
                              <td className="px-4 py-3 text-right text-sm">{formatCurrency(station.metrics?.daily_avg_revenue)}</td>
                              <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(station.metrics?.total_revenue)}</td>
                              <td className={`px-4 py-3 text-right text-sm ${station.efficiency?.loss_percentage > 1 ? 'text-red-600' : 'text-green-600'}`}>
                                {station.efficiency?.loss_percentage?.toFixed(2)}%
                              </td>
                              <td className={`px-4 py-3 text-right text-sm ${station.efficiency?.expense_ratio > 10 ? 'text-amber-600' : ''}`}>
                                {station.efficiency?.expense_ratio?.toFixed(2)}%
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-green-600">
                                {station.efficiency?.credit_collection_rate?.toFixed(1)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  </div>
                ) : (
                  <Card className="p-12 text-center border border-dashed border-border">
                    <BarChart3 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-heading font-semibold mb-2">No Analytics Data</h3>
                    <p className="text-muted-foreground">Select a period and click Refresh to load analytics.</p>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            {/* Station Detail Modal */}
            <Dialog open={!!selectedStation} onOpenChange={() => setSelectedStation(null)}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{selectedStation?.pump_name}</DialogTitle>
                </DialogHeader>
                {selectedStation && (
                  <div className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted">Location</p>
                        <p className="font-medium">{selectedStation.location}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted">Town</p>
                        <p className="font-medium">{selectedStation.town}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted">District</p>
                        <p className="font-medium">{selectedStation.district}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted">State</p>
                        <p className="font-medium">{selectedStation.state}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted">Mobile</p>
                        <p className="font-medium">{selectedStation.mobile}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted">Status</p>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          selectedStation.account_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {selectedStation.account_status}
                        </span>
                      </div>
                    </div>

                    {stationStats[selectedStation.id] && (
                      <>
                        <Card className="p-4 bg-secondary">
                          <h4 className="font-semibold mb-3">Performance Summary</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-muted">Yesterday's Revenue</p>
                              <p className="font-bold text-green-600">
                                {formatCurrency(stationStats[selectedStation.id].yesterday_revenue)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted">Credit Outstanding</p>
                              <p className="font-bold text-amber-600">
                                {formatCurrency(stationStats[selectedStation.id].credit_outstanding)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted">Credit (7 Days)</p>
                              <p className="font-bold text-emerald-600">
                                {formatCurrency(stationStats[selectedStation.id].credit_collected_7d)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted">Credit (Month)</p>
                              <p className="font-bold text-blue-600">
                                {formatCurrency(stationStats[selectedStation.id].credit_collected_month)}
                              </p>
                            </div>
                          </div>
                        </Card>

                        <div className="grid grid-cols-4 gap-3">
                          <Card className="p-3 text-center">
                            <p className="text-xs text-muted">MS Stock</p>
                            <p className="font-bold">{stationStats[selectedStation.id].ms_stock?.toLocaleString() || 0} L</p>
                          </Card>
                          <Card className="p-3 text-center">
                            <p className="text-xs text-muted">HSD Stock</p>
                            <p className="font-bold">{stationStats[selectedStation.id].hsd_stock?.toLocaleString() || 0} L</p>
                          </Card>
                          <Card className="p-3 text-center bg-red-50">
                            <p className="text-xs text-muted">MS Loss (7d)</p>
                            <p className="font-bold text-red-600">{stationStats[selectedStation.id].ms_loss_7d?.toFixed(1) || 0} L</p>
                          </Card>
                          <Card className="p-3 text-center bg-red-50">
                            <p className="text-xs text-muted">HSD Loss (7d)</p>
                            <p className="font-bold text-red-600">{stationStats[selectedStation.id].hsd_loss_7d?.toFixed(1) || 0} L</p>
                          </Card>
                        </div>
                      </>
                    )}

                    {selectedStation.id !== currentPumpId && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800 mb-3">
                          Switch to this station to access its full dashboard and manage operations.
                        </p>
                        <Button 
                          className="w-full"
                          onClick={() => handleLoginToStation(selectedStation)}
                        >
                          <LogIn className="w-4 h-4 mr-2" />
                          Switch to {selectedStation.pump_name}
                        </Button>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <Button variant="outline" onClick={() => setSelectedStation(null)}>Close</Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default MultiStationDashboard;
